import React, { useEffect, useMemo, useState } from "react";
import Nav from "../components/Nav/Nav";
import { ArrowLeftOutlined, LoadingOutlined } from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { refundPayment } from "../api/backend";
import "../App.css";
import "../style/OrderManagement.css";
import { initPushNotifications } from "../services/notifications";

function OrderManagement() {
  const navigate = useNavigate();
  const { id } = useParams();

  const {
    currentUser,
    currentRestaurant,
    getRestaurant,
    getRestaurantOrders,
    ordersList,
    updateOrderStatus,
    isLoading,
  } = useAuth();

  const [tab, setTab] = useState("new");
  const [refundLoading, setRefundLoading] = useState({
    id: "",
    type: "",
  });
  const [refundOpenId, setRefundOpenId] = useState("");
  const [partialRefundValue, setPartialRefundValue] = useState({});
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [openNotifModal, setOpenNotifModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;

const isIOS = /iPad|iPhone|iPod/.test(userAgent);
const isAndroid = /Android/.test(userAgent);

const getNotificationSteps = () => {
  if (isIOS) {
    return [
      "Ouvrez ce site dans Safari (pas Chrome)",
      "Cliquez sur le bouton Partager (carré avec flèche)",
      "Appuyez sur 'Ajouter à l’écran d’accueil'",
      "Ouvrez l’application depuis l’icône",
      "Activez les notifications"
    ];
  }

  if (isAndroid) {
    return [
      "Ouvrez ce site dans Google Chrome",
      "Cliquez sur 'Autoriser' quand la popup apparaît",
      "Paramètres du téléphone > Applications > Chrome",
      "Notifications > Activer"
    ];
  }

  return [
    "Utilisez Google Chrome",
    "Cliquez sur 'Autoriser' quand la popup apparaît",
    "Paramètres > Notifications > Google Chrome > Autoriser",
    "Rechargez la page"
  ];
};

  useEffect(() => {
    getRestaurant(id);
    const unsubscribe = getRestaurantOrders(id);

    return () => {
      if (typeof unsubscribe === "function") unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (
      currentUser &&
      currentRestaurant &&
      currentRestaurant.owner !== currentUser.uid
    ) {
      navigate("/");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, currentRestaurant]);

  useEffect(() => {
    if (typeof Notification !== "undefined" && Notification.permission === "granted") {
      setNotificationsEnabled(true);
    }
  }, []);


  

  const newCount =
    ordersList?.filter((order) => order.status === "paid").length || 0;

  const progressCount =
    ordersList?.filter(
      (order) => order.status === "preparing" || order.status === "ready"
    ).length || 0;

  const deliveryCount =
    ordersList?.filter((order) => order.status === "out_for_delivery").length || 0;

  const doneCount =
    ordersList?.filter(
      (order) =>
        order.status === "completed" ||
        order.status === "cancelled" ||
        order.status === "delivered"
    ).length || 0;

  const filteredOrders = useMemo(() => {
    if (tab === "new") {
      return ordersList?.filter((order) => order.status === "paid") || [];
    }

    if (tab === "progress") {
      return (
        ordersList?.filter(
          (order) => order.status === "preparing" || order.status === "ready"
        ) || []
      );
    }

    if (tab === "delivery") {
      return (
        ordersList?.filter(
          (order) => order.status === "out_for_delivery"
        ) || []
      );
    }

    return (
      ordersList?.filter(
        (order) =>
          order.status === "completed" ||
          order.status === "cancelled" ||
          order.status === "delivered"
      ) || []
    );
  }, [ordersList, tab]);

  const formatAmount = (value) =>
    `${(Number(value || 0) / 100).toFixed(2)} €`;

  const formatDate = (createdAt) => {
    if (!createdAt) return "";

    const date = createdAt?.seconds
      ? new Date(createdAt.seconds * 1000)
      : new Date(createdAt);

    return date.toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusLabel = (status) => {
    if (status === "paid") return "Nouvelle";
    if (status === "preparing") return "En préparation";
    if (status === "ready") return "Prête";
    if (status === "out_for_delivery") return "En livraison";
    if (status === "delivered") return "Livrée";
    if (status === "completed") return "Terminée";
    if (status === "cancelled") return "Annulée";
    return status;
  };

  const getNextStatus = (order) => {
    if (order.status === "paid") return "preparing";
    if (order.status === "preparing") return "ready";

    if (order.status === "ready") {
      return order.serviceType === "delivery" ? "out_for_delivery" : "completed";
    }

    if (order.status === "out_for_delivery") return "delivered";

    return null;
  };

  const getNextButtonLabel = (order) => {
    if (order.status === "paid") return "Préparer la commande";
    if (order.status === "preparing") return "Marquer prête";

    if (order.status === "ready") {
      return order.serviceType === "delivery" ? "Passer en livraison" : "Terminer";
    }

    if (order.status === "out_for_delivery") return "Marquer livrée";

    return "";
  };

  const getOrderNumber = (order) => {
    return order.id?.slice(-6)?.toUpperCase() || order.id;
  };

  const getMapsUrl = (address) => {
    if (!address) return "#";
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  };

  const withCount = (label, count) => {
    return count > 0 ? `${label} (${count})` : label;
  };

  const getRefundedAmount = (order) => Number(order.refundedAmount || 0);
  const getOrderAmount = (order) => Number(order.amount || 0);
  const getRemainingRefundable = (order) =>
    Math.max(getOrderAmount(order) - getRefundedAmount(order), 0);

  const canCancelOrder = (order) =>
    ["paid", "preparing", "ready", "out_for_delivery"].includes(order.status) &&
    !!order.paymentIntentId &&
    getRemainingRefundable(order) > 0;

    const canRefundFinishedOrder = (order) =>
    false;

  const handleCancelOrder = (order) => setConfirmAction({ order, type: "cancel" });

  const handleFullRefund = (order) => setConfirmAction({ order, type: "full" });

  const runCancelOrder = async (order) => {
    try {
      setRefundLoading({ id: order.id, type: "cancel" });

      await refundPayment({
        orderId: order.id,
        reason: "requested_by_customer",
        refundApplicationFee: true,
      });
    } catch (error) {
      alert(error.message || "Impossible d’annuler la commande");
    } finally {
      setRefundLoading({ id: "", type: "" });
    }
  };

  const runFullRefund = async (order) => {
    try {
      setRefundLoading({ id: order.id, type: "full" });

      await refundPayment({
        orderId: order.id,
        reason: "requested_by_customer",
        refundApplicationFee: true,
      });

      setRefundOpenId("");
    } catch (error) {
      alert(error.message || "Impossible d’effectuer le remboursement");
    } finally {
      setRefundLoading({ id: "", type: "" });
    }
  };

  const handlePartialRefund = async (order) => {
    try {
      const rawValue = partialRefundValue[order.id];
      const euros = Number(rawValue);

      if (!rawValue || Number.isNaN(euros) || euros <= 0) {
        alert("Entrez un montant valide");
        return;
      }

      const amountInCents = Math.round(euros * 100);
      const remaining = getRemainingRefundable(order);

      if (amountInCents > remaining) {
        alert("Le montant dépasse le reste remboursable");
        return;
      }

      setRefundLoading({ id: order.id, type: "partial" });

      await refundPayment({
        orderId: order.id,
        amount: amountInCents,
        reason: "requested_by_customer",
        refundApplicationFee: true,
      });

      setPartialRefundValue((prev) => ({
        ...prev,
        [order.id]: "",
      }));
      setRefundOpenId("");
    } catch (error) {
      alert(error.message || "Impossible d’effectuer le remboursement");
    } finally {
      setRefundLoading({ id: "", type: "" });
    }
  };

  return (
    <div className="one-page">
      <Nav />
      <div className="main">
        <div className="wrapper-container">
          <div className="wrapper-content">
          <div className="title-wrapper">
  <div className="title-back">
    <ArrowLeftOutlined
      onClick={() => navigate(-1)}
      className="back"
    />
    <p>Gestion des commandes</p>
  </div>

  {!notificationsEnabled && (
    <button
      type="button"
      className="button title-button-secondary"
      onClick={() => setOpenNotifModal(true)}
    >
      Activer les notifications
    </button>
  )}

{openNotifModal && (
  <div className="notif-overlay" onClick={() => setOpenNotifModal(false)}>
    <div
      className="notif-modal"
      onClick={(e) => e.stopPropagation()}
    >
      <h2 className="notif-title">Activer les notifications</h2>

      <p className="notif-subtitle">
        Pour recevoir les nouvelles commandes en temps réel :
      </p>
      <ol className="notif-list">
  {getNotificationSteps().map((step, index) => (
    <li key={index}>{step}</li>
  ))}
</ol>

      <div className="notif-actions">
        <button
          type="button"
          className="notif-btn primary"
          onClick={async () => {
            if (isIOS && !window.navigator.standalone) {
              alert("Sur iPhone, ajoutez l’app à l’écran d’accueil via Safari.");
              return;
            }

            const res = await initPushNotifications(id);

            if (res.success) {
              setNotificationsEnabled(true);
              setOpenNotifModal(false);
            } else if (res.reason === "denied") {
              alert(
                "Notifications bloquées.\n\n" +
                "Allez dans les paramètres du navigateur et autorisez-les."
              );
            } else {
              alert("Impossible d’activer les notifications");
            }
          }}
        >
          Activer les notifications
        </button>

        <button
          type="button"
          className="notif-btn secondary"
          onClick={() => setOpenNotifModal(false)}
        >
          Fermer
        </button>
      </div>
    </div>
  </div>
)}

{confirmAction && (
  <div className="notif-overlay" onClick={() => setConfirmAction(null)}>
    <div className="notif-modal" onClick={(e) => e.stopPropagation()}>
      <h2 className="notif-title">
        {confirmAction.type === "cancel" ? "Annuler la commande ?" : "Rembourser la commande ?"}
      </h2>
      <p className="notif-subtitle">
        {confirmAction.type === "cancel"
          ? "Le client sera remboursé en totalité. Cette action est irréversible."
          : "Le client sera remboursé du montant restant. Cette action est irréversible."}
      </p>
      <div className="notif-actions">
        <button
          type="button"
          className="notif-btn primary"
          onClick={() => {
            const { order, type } = confirmAction;
            setConfirmAction(null);
            if (type === "cancel") runCancelOrder(order);
            else runFullRefund(order);
          }}
        >
          {confirmAction.type === "cancel" ? "Oui, annuler" : "Oui, rembourser"}
        </button>
        <button
          type="button"
          className="notif-btn secondary"
          onClick={() => setConfirmAction(null)}
        >
          Fermer
        </button>
      </div>
    </div>
  </div>
)}
</div>

            <div className="wrapper-form">
              <div className="order-management-tabs">
                <button
                  type="button"
                  className={`order-management-tab ${tab === "new" ? "active" : ""}`}
                  onClick={() => setTab("new")}
                >
                  {withCount("Nouvelles", newCount)}
                </button>

                <button
                  type="button"
                  className={`order-management-tab ${tab === "progress" ? "active" : ""}`}
                  onClick={() => setTab("progress")}
                >
                  {withCount("En préparation", progressCount)}
                </button>

                <button
                  type="button"
                  className={`order-management-tab ${tab === "delivery" ? "active" : ""}`}
                  onClick={() => setTab("delivery")}
                >
                  {withCount("En livraison", deliveryCount)}
                </button>

                <button
                  type="button"
                  className={`order-management-tab ${tab === "done" ? "active" : ""}`}
                  onClick={() => setTab("done")}
                >
                  {withCount("Terminées", doneCount)}
                </button>
              </div>

              {isLoading ? (
                <div className="order-management-loading">
                  <LoadingOutlined spin />
                </div>
              ) : filteredOrders.length === 0 ? (
                <div className="order-management-empty">
                  Aucune commande
                </div>
              ) : (
                <div className="order-management-list">
                  {filteredOrders.map((order) => (
                    <div key={order.id} className="order-management-card">
                      <div className="order-management-card-top">
                        <div>
                          <p className="order-management-card-title">
                            Commande n°{getOrderNumber(order)}
                          </p>

                          <p className="order-management-item-sub">
                            {order.serviceType === "table"
                              ? `À table · Table ${order.tableNumber || "-"}`
                              : order.serviceType === "delivery"
                              ? "Livraison à domicile"
                              : "À emporter"}
                          </p>

                          {order.serviceType === "delivery" && order.deliveryAddress ? (
                            <a
                              href={getMapsUrl(order.deliveryAddress)}
                              target="_blank"
                              rel="noreferrer"
                              className="order-management-item-sub"
                              style={{ textDecoration: "underline" }}
                            >
                              {order.deliveryAddress}
                            </a>
                          ) : null}

                          <p className="order-management-card-subtitle">
                            {order.firstName || "Client"} · {formatDate(order.createdAt)}
                          </p>

                          {order.customerPhone ? (
                            <a
                              href={`tel:${order.customerPhone}`}
                              className="order-management-item-sub"
                              style={{ textDecoration: "underline" }}
                            >
                              {order.customerPhone}
                            </a>
                          ) : null}

                          {order.refundStatus === "full" ? (
                            <p className="order-management-item-sub">
                              Commande totalement remboursée
                            </p>
                          ) : null}

                          {order.refundStatus === "partial" ? (
                            <p className="order-management-item-sub">
                              Commande partiellement remboursée
                            </p>
                          ) : null}

                          {getRefundedAmount(order) > 0 ? (
                            <p className="order-management-item-sub">
                              Remboursé : {formatAmount(getRefundedAmount(order))}
                            </p>
                          ) : null}
                        </div>

                        <div className="order-management-card-status">
                          {getStatusLabel(order.status)}
                        </div>
                      </div>

                      <div className="order-management-divider" />

                      <div className="order-management-items">
                        {order.items?.map((item, index) => (
                          <div key={index} className="order-management-item">
                            <div className="order-management-item-left">
                              <p className="order-management-item-name">
                                {item.quantity}× {item.productName}
                              </p>

                              {item.optionName && (
                                <p className="order-management-item-sub">
                                  {item.optionName}
                                </p>
                              )}

                              {item.supplementNames?.length > 0 && (
                                <div className="order-management-item-sub">
                                  {item.supplementNames.map((supplement, i) => (
                                    <div key={i}>{supplement}</div>
                                  ))}
                                </div>
                              )}
                            </div>

                            <div className="order-management-item-price">
                              {formatAmount(item.lineTotal)}
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="order-management-divider" />

                      <div className="order-management-footer">
                        <div className="order-management-totals">
                          <p>
                            Sous-total : {formatAmount(order.subtotalAmount)}
                          </p>

                          {Number(order.tipAmount || 0) > 0 && (
                            <p>
                              Pourboire : {formatAmount(order.tipAmount)}
                            </p>
                          )}

                          <p className="order-management-total">
                            Total : {formatAmount(order.totalAmount || order.amount)}
                          </p>

                          {(canCancelOrder(order) || canRefundFinishedOrder(order)) ? (
                            <p>
                              Reste remboursable :{" "}
                              {formatAmount(getRemainingRefundable(order))}
                            </p>
                          ) : null}
                        </div>

                        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                          {getNextStatus(order) && (
                            <button
                              type="button"
                              className="button save order-font"
                              onClick={() =>
                                updateOrderStatus(order.id, getNextStatus(order))
                              }
                            >
                              {getNextButtonLabel(order)}
                            </button>
                          )}

                          {canCancelOrder(order) && (
                            <button
                              type="button"
                              className="button title-button-secondary order-font"
                              onClick={() => handleCancelOrder(order)}
                              disabled={
                                refundLoading.id === order.id &&
                                refundLoading.type === "cancel"
                              }
                            >
                              {refundLoading.id === order.id &&
                              refundLoading.type === "cancel"
                                ? "Chargement..."
                                : "Annuler la commande"}
                            </button>
                          )}

                          {canRefundFinishedOrder(order) && (
                            <button
                              type="button"
                              className="button title-button-secondary order-font"
                              onClick={() =>
                                setRefundOpenId((prev) =>
                                  prev === order.id ? "" : order.id
                                )
                              }
                            >
                              Rembourser
                            </button>
                          )}
                        </div>
                      </div>

                      {refundOpenId === order.id && canRefundFinishedOrder(order) && (
                        <>
                          <div className="order-management-divider" />

                          <div className="refund-container">
                            <div className="refund-buttons-row">
                              <button
                                type="button"
                                className="button refund-full"
                                onClick={() => handleFullRefund(order)}
                                disabled={
                                  refundLoading.id === order.id &&
                                  refundLoading.type === "full"
                                }
                              >
                                {refundLoading.id === order.id &&
                                refundLoading.type === "full"
                                  ? "Chargement..."
                                  : "Remboursement total"}
                              </button>

                              <button
                                type="button"
                                className="button title-button-secondary refund-partial order-font"
                                onClick={() => handlePartialRefund(order)}
                                disabled={
                                  (refundLoading.id === order.id &&
                                    refundLoading.type === "partial") ||
                                  !partialRefundValue[order.id]
                                }
                              >
                                {refundLoading.id === order.id &&
                                refundLoading.type === "partial"
                                  ? "Chargement..."
                                  : "Remboursement partiel"}
                              </button>
                            </div>

                            <div className="refund-input-row">
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                placeholder="Montant (€)"
                                value={partialRefundValue[order.id] || ""}
                                onChange={(e) =>
                                  setPartialRefundValue((prev) => ({
                                    ...prev,
                                    [order.id]: e.target.value,
                                  }))
                                }
                                className="refund-input"
                              />
                            </div>
                          </div>
                        </>
                      )}

                      {order.comment && (
                        <div className="order-management-comment">
                          {order.comment}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OrderManagement;